// DateField — the one date control of the app (owner requirement 05, Oct 2026). Replaces every <input type="date">:
// the native control looked and behaved differently in Chromium, WebView2 (Windows) and WKWebView (macOS), followed the
// OS region instead of the app's language, and handed partial values (0002-…, 0020-…) to React on every keystroke.
//
//   • three segments — day, month, year — each typed or changed on its own, in the order of the app's locale
//     (el/ru: ΗΗ/ΜΜ/ΕΕΕΕ; English as written in Cyprus: DD/MM/YYYY — see dateFieldLocale);
//   • a whole date typed or pasted into any segment («15/06/2000», «15062000», ISO «2000-06-15») fills all three;
//   • keyboard: digits move on by themselves, «/ . -» or → go to the next segment, ← / Backspace on an empty segment go
//     back, ↑ ↓ step (wrapping day and month), PgUp / PgDn move the year by 10;
//   • impossible dates, 29 February outside leap years, and min / max are refused with an inline message;
//   • value contract unchanged: `value` and `onChange` carry an ISO 'YYYY-MM-DD' string or ''. While the draft is not a
//     full valid date nothing is emitted: the parent keeps the last valid value (Save never erases a stored date by
//     accident) and the message is shown. '' is emitted only when the advisor clears the field (× or every part emptied).
// Pure logic: core/date-parts.ts (unit-tested).
import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { useRuntime } from './runtime.tsx';
import {
  checkDraft, cleanSegment, dateBounds, dateFieldLocale, emission, parseTyped, segmentDone, segmentOrder, segmentSeparator, splitIso, stepSegment,
  type DateDraft, type DateIssue, type Segment,
} from './date-parts.ts';

export interface DateFieldProps {
  value: string | null | undefined;
  onChange: (iso: string) => void;
  min?: string | null;
  max?: string | null;
  /** 'birth' = 1900-01-01 … today (plus any min / max given) */
  kind?: 'birth' | 'any';
  /** accessible name of the group when the field is not inside <Field group> */
  label?: string;
  testId?: string;
  size?: 'sm';
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
}

type InputEv = { target: HTMLInputElement };

export function DateField({ value, onChange, min, max, kind, label, testId, size, className, style, disabled }: DateFieldProps) {
  const rt = useRuntime();
  const { t } = rt;
  const order = useMemo(() => segmentOrder(dateFieldLocale(rt.i18n.locale)), [rt.i18n.locale]);
  const sep = useMemo(() => segmentSeparator(dateFieldLocale(rt.i18n.locale)), [rt.i18n.locale]);
  const bounds = useMemo(() => dateBounds({ kind, min, max, today: rt.today }), [kind, min, max, rt.today]);
  const [draft, setDraft] = useState<DateDraft>(() => splitIso(value));
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);
  // the last value this field emitted (or received): a parent re-rendering with it does not reset the draft
  const last = useRef(value ?? '');
  // the value when this round of editing began (focus came in): what an invalid draft falls back to
  const base = useRef(value ?? '');
  useEffect(() => {
    const v = value ?? '';
    if (v !== last.current) { last.current = v; base.current = v; setDraft(splitIso(v)); setTouched(false); }
  }, [value]);
  const dayRef = useRef<HTMLInputElement | null>(null);
  const monthRef = useRef<HTMLInputElement | null>(null);
  const yearRef = useRef<HTMLInputElement | null>(null);
  const refs: Record<Segment, { current: HTMLInputElement | null }> = { day: dayRef, month: monthRef, year: yearRef };
  const wrap = useRef<HTMLSpanElement | null>(null);
  // segments typed into since they got focus: a separator there moves on; right after an automatic move it is ignored
  const typed = useRef<Record<Segment, boolean>>({ day: false, month: false, year: false });
  const errId = useId();

  const check = checkDraft(draft, bounds);
  const showIssue: DateIssue | null = check.issue && (check.issue !== 'incomplete' || touched) ? check.issue : null;

  const commit = (d: DateDraft) => {
    setDraft(d);
    const out = emission(checkDraft(d, bounds), last.current, base.current);
    if (out !== null) { last.current = out; onChange(out); }
  };
  const focusSeg = (seg: Segment | undefined) => { const el = seg ? refs[seg].current : null; if (el) { el.focus(); el.select(); } };
  const next = (seg: Segment) => order[order.indexOf(seg) + 1];
  const prev = (seg: Segment) => order[order.indexOf(seg) - 1];

  const onInput = (seg: Segment) => (e: InputEv) => {
    const raw = e.target.value;
    const whole = raw.length > 4 ? parseTyped(raw, order) : null;
    if (whole) { commit(whole); focusSeg(order[2]); return; }
    const v = cleanSegment(seg, raw);
    typed.current[seg] = true;
    commit({ ...draft, [seg]: v });
    if (v.length > draft[seg].length && segmentDone(seg, v)) focusSeg(next(seg));
  };
  const onKey = (seg: Segment) => (e: KeyboardEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    const k = e.key;
    if (k === 'ArrowUp' || k === 'ArrowDown' || k === 'PageUp' || k === 'PageDown') {
      e.preventDefault();
      const step = (k === 'ArrowUp' || k === 'PageUp' ? 1 : -1) * (seg === 'year' && (k === 'PageUp' || k === 'PageDown') ? 10 : 1);
      commit(stepSegment(draft, seg, step, bounds, rt.today));
      requestAnimationFrame(() => refs[seg].current?.select());
    } else if (k === '/' || k === '.' || k === '-' || (k === ' ' && seg !== 'year')) {
      e.preventDefault();
      if (!typed.current[seg] || !draft[seg]) return;
      if (draft[seg].length === 1 && seg !== 'year') commit({ ...draft, [seg]: draft[seg].padStart(2, '0') });
      focusSeg(next(seg));
    } else if (k === 'Backspace' && !el.value && prev(seg)) {
      e.preventDefault(); focusSeg(prev(seg));
    } else if (k === 'ArrowLeft' && el.selectionStart === 0 && el.selectionEnd === 0 && prev(seg)) {
      e.preventDefault(); focusSeg(prev(seg));
    } else if (k === 'ArrowRight' && el.selectionStart === el.value.length && next(seg)) {
      e.preventDefault(); focusSeg(next(seg));
    }
  };
  const onBlur = () => {
    // focus leaving the whole control (not moving between its segments)
    setTimeout(() => { if (!wrap.current?.contains(document.activeElement)) { setFocused(false); setTouched(true); } }, 0);
  };

  const issueText = (i: DateIssue) => {
    switch (i) {
      case 'year': return t('dates.err_year', { min: String(bounds.yearMin), max: String(bounds.yearMax) });
      case 'no_such_day': return t('dates.err_no_such_day', { days: String(new Date(Date.UTC(+draft.year, +draft.month, 0)).getUTCDate()) });
      case 'feb29': return t('dates.err_feb29', { year: draft.year });
      case 'before_min': return t('dates.err_before', { date: rt.i18n.date(bounds.min) });
      case 'after_max': return t('dates.err_after', { date: rt.i18n.date(bounds.max) });
      default: return t(`dates.err_${i}`);
    }
  };
  const segAttrs: Record<Segment, { cls: string; ph: string; name: string }> = {
    day: { cls: 'df-d', ph: t('dates.ph_day'), name: t('dates.day') },
    month: { cls: 'df-m', ph: t('dates.ph_month'), name: t('dates.month') },
    year: { cls: 'df-y', ph: t('dates.ph_year'), name: t('dates.year') },
  };
  const hasAny = !!(draft.day || draft.month || draft.year);
  return (
    <span className={`datefield-wrap${className ? ' ' + className : ''}`} style={style}>
      <span ref={wrap} className={`input datefield${size === 'sm' ? ' input-sm' : ''}`} data-testid={testId} data-value={check.value} data-invalid={showIssue ? 'true' : undefined}
        aria-invalid={showIssue ? true : undefined} {...(label ? { role: 'group', 'aria-label': label } : {})}
        onFocus={() => { if (!focused) base.current = last.current; setFocused(true); }} onBlur={onBlur}>
        {order.map((seg, i) => (
          <span key={seg} className="df-seg">
            {i > 0 && <span className="df-sep" aria-hidden="true">{sep}</span>}
            <input ref={refs[seg]} className={`df-in ${segAttrs[seg].cls}`} inputMode="numeric" autoComplete="off" spellCheck={false} disabled={disabled}
              value={draft[seg]} placeholder={segAttrs[seg].ph} aria-label={segAttrs[seg].name} aria-invalid={showIssue ? true : undefined}
              aria-describedby={showIssue ? errId : undefined} title={seg === 'year' ? t('dates.year_hint') : undefined}
              data-seg={seg} data-testid={testId ? `${testId}-${seg}` : undefined}
              onChange={onInput(seg)} onKeyDown={onKey(seg)} onFocus={(e: { target: HTMLInputElement }) => { typed.current[seg] = false; e.target.select(); }} />
          </span>
        ))}
        {hasAny && !disabled && <button type="button" className="df-clear" tabIndex={-1} aria-label={t('dates.clear')} title={t('dates.clear')}
          onMouseDown={(e: { preventDefault(): void }) => e.preventDefault()} onClick={() => { commit({ day: '', month: '', year: '' }); setTouched(false); focusSeg(order[0]); }}>×</button>}
      </span>
      {showIssue
        ? <small id={errId} className="datefield-error" role="alert" data-testid={testId ? `${testId}-error` : undefined}>{issueText(showIssue)}</small>
        : focused && check.value ? <small className="datefield-preview muted" aria-hidden="true">{rt.i18n.date(check.value, 'long')}</small> : null}
    </span>
  );
}
